import { useState, useEffect, useMemo } from 'react';
import {
  fetchCompletedVistaJudgments,
  fetchVistaAspects,
  fetchVistaRubrics,
} from 'services/judgmentService';
import {
  mergeJudgmentData,
  transformStrapiDataToTableFormat,
} from 'services/judgmentDataTransformer';
import { getQualificationSubmissions } from 'services/qualificationEvaluatorService';
import { getFormSchema } from 'forms';
import { useAuth } from 'contexts/AuthContext';
import { Submission } from 'types/evaluation';
import { useRoleInfo } from 'hooks/useRoleInfo';
import { getPillarDisplayName } from 'constants/awardCategoryPillar';
import {
  filterBySearchQuery,
  filterByCategory,
  sortSubmissions,
} from 'utils/submissionSorting';

const currentYear = new Date().getFullYear();

export const useQualificationJudgmentData = () => {
  const [year, setYear] = useState<number>(currentYear);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [sortField, setSortField] = useState<string>('avg_rank');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set()
  );
  // eslint-disable-next-line no-unused-vars
  const [aspects, setAspects] = useState<any[]>([]);

  const [rubrics, setRubrics] = useState<any[]>([]);

  // Get authenticated user info and role data from localStorage
  useAuth();

  const {
    userRoleType,
    userPillar,
    userCategory,
    userIsSuperAdmin,
    userIsPillarPIC,
    userIsCategoryJudge,
  } = useRoleInfo();

  // Determine access control based on role type - ADMIN AND PILLAR PIC ACCESS
  const userHasAccess = userIsSuperAdmin || userIsPillarPIC;

  const loadQualificationSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        qualificationSubmissionsResponse,
        judgmentsResponse,
        aspectsResponse,
        rubricsResponse,
      ]: any = await Promise.all([
        getQualificationSubmissions(year),
        fetchCompletedVistaJudgments(year),
        fetchVistaAspects(year),
        fetchVistaRubrics(year),
      ]);

      const submissionsData =
        qualificationSubmissionsResponse.data ||
        qualificationSubmissionsResponse;
      const judgmentsData = judgmentsResponse.data || judgmentsResponse;
      const aspectsData = aspectsResponse.data || aspectsResponse;
      const rubricsData = rubricsResponse.data || rubricsResponse;

      const transformedSubmissions =
        transformStrapiDataToTableFormat(submissionsData);
      const submissionsWithJudgments = mergeJudgmentData(
        transformedSubmissions,
        judgmentsData,
        aspectsData,
        rubricsData
      );

      // Since we're only fetching completed judgments, all submissions with judgments are valid
      const completedSubmissions = submissionsWithJudgments.filter(
        submission =>
          submission.all_judgments && submission.all_judgments.length > 0
      );

      setSubmissions(completedSubmissions);
      setAspects(aspectsData);
      setRubrics(rubricsData);
    } catch (err: any) {
      console.error('❌ Error loading qualification judgment data:', err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQualificationSubmissions();
  }, [year]);

  const handleRefresh = async () => {
    await loadQualificationSubmissions();
  };

  const updateSubmissionStageLocal = (documentId: string, newStage: string) => {
    setSubmissions(prev =>
      prev.map(s =>
        s.documentId === documentId ? { ...s, competition_stage: newStage } : s
      )
    );
  };

  const toggleRowExpansion = (id: string | number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const schema = getFormSchema('smdd');
  const categories = schema.categories;

  const filteredAndSortedSubmissions = useMemo(() => {
    let filtered: Submission[] = submissions;

    if (searchQuery) {
      filtered = filterBySearchQuery(filtered, searchQuery);
    }

    if (filterCategory) {
      filtered = filterByCategory(filtered, filterCategory);
    }

    if (userIsPillarPIC && userPillar) {
      const displayUserPillar = getPillarDisplayName(userPillar);
      filtered = filtered.filter(
        s => s.pillar === userPillar || s.pillar === displayUserPillar
      );
    } else if (userIsCategoryJudge && userCategory) {
      filtered = filtered.filter(s => s.category === userCategory);
    }

    return sortSubmissions(filtered, submissions, sortField, sortDirection);
  }, [
    submissions,
    searchQuery,
    filterCategory,
    sortField,
    sortDirection,
    userIsSuperAdmin,
    userIsPillarPIC,
    userIsCategoryJudge,
    userPillar,
    userCategory,
  ]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = filteredAndSortedSubmissions.length;
    const judged = filteredAndSortedSubmissions.filter(
      s => s.total_score !== null && s.total_score !== undefined
    ).length;

    // Calculate total evaluations (same as Raw Judgment sheet records)
    // Each submission without judgments = 1 row, each judgment = 1 row
    const totalEvaluations = filteredAndSortedSubmissions.reduce(
      (count, submission) => {
        const allJudgments = submission.all_judgments || [];
        if (allJudgments.length === 0) {
          return count + 1; // One row for submission without judgments
        } else {
          return count + allJudgments.length; // One row per judgment
        }
      },
      0
    );

    const avgScore =
      judged > 0
        ? filteredAndSortedSubmissions
            .filter(s => s.total_score !== null && s.total_score !== undefined)
            .reduce((sum, s) => sum + (Number(s.total_score) || 0), 0) / judged
        : 0;

    return {
      total,
      judged,
      totalEvaluations,
      averageScore: Math.round(avgScore * 100) / 100,
    };
  }, [filteredAndSortedSubmissions]);

  const getMainDescription = (submission: Submission) => {
    return (
      submission.leadership_achievement ||
      submission.project_name ||
      'No description available'
    );
  };

  const sanitizeAIText = (text: string | null | undefined) => {
    if (!text) return 'No feedback available';
    return text.replace(/[*#]/g, '').trim();
  };

  return {
    submissions,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filterCategory,
    setFilterCategory,
    year,
    setYear,
    sortField,
    sortDirection,
    expandedRows,
    handleRefresh,
    toggleRowExpansion,
    handleSort,
    categories,
    filteredAndSortedSubmissions,
    stats,
    rubrics,
    getMainDescription,
    sanitizeAIText,
    updateSubmissionStageLocal,
    // Access control
    userHasAccess,
    userPillar,
    userIsSuperAdmin,
    userIsPillarPIC,
    userIsCategoryJudge,
    userCategory,
  };
};
